import React, { useState, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';

const TimePicker = ({ value, onChange, label = "Time" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('input'); // 'clock' or 'input'
  const [selectingMinutes, setSelectingMinutes] = useState(false);
  
  // Parse the value (HH:mm format) into hour, minute, and period
  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: 12, minute: 0, period: 'AM' };
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    let hour = hours % 12;
    if (hour === 0) hour = 12;
    
    return { hour, minute: minutes || 0, period };
  };

  const [time, setTime] = useState(parseTime(value));

  // Update internal state when value prop changes
  useEffect(() => {
    setTime(parseTime(value));
  }, [value]);

  // Convert back to 24-hour format for the onChange callback
  const formatTime = (hour, minute, period) => {
    let h = hour;
    if (period === 'AM' && hour === 12) h = 0;
    else if (period === 'PM' && hour !== 12) h = hour + 12;
    
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const handleTimeChange = (newTime) => {
    setTime(newTime);
    const formattedTime = formatTime(newTime.hour, newTime.minute, newTime.period);
    onChange(formattedTime);
  };

  const handleHourChange = (newHour) => {
    let h = newHour;
    if (h > 12) h = 1;
    if (h < 1) h = 12;
    handleTimeChange({ ...time, hour: h });
  };

  const handleMinuteChange = (newMinute) => {
    let m = newMinute;
    if (m > 59) m = 0;
    if (m < 0) m = 59;
    handleTimeChange({ ...time, minute: m });
  };

  const togglePeriod = (newPeriod) => {
    handleTimeChange({ ...time, period: newPeriod });
  };

  // Format display time
  const displayTime = value ? 
    `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')} ${time.period}` : 
    'Select time';

  // Clock dial component
  const ClockDial = () => {
    const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    
    const currentValue = selectingMinutes ? time.minute : time.hour;
    const items = selectingMinutes ? minutes : hours;
    
    const handleClockClick = (value) => {
      if (selectingMinutes) {
        handleMinuteChange(value);
      } else {
        handleHourChange(value);
        setSelectingMinutes(true);
      }
    };

    // Calculate angle for the clock hand
    const getAngle = () => {
      if (selectingMinutes) {
        return (time.minute / 60) * 360 - 90;
      } else {
        return ((time.hour % 12) / 12) * 360 - 90;
      }
    };

    return (
      <div className="relative w-56 h-56 mx-auto">
        {/* Clock face */}
        <div className="absolute inset-0 rounded-full bg-gray-100 dark:bg-gray-700">
          {/* Clock hand */}
          <div 
            className="absolute top-1/2 left-1/2 origin-left h-0.5 bg-purple-500"
            style={{
              width: '35%',
              transform: `rotate(${getAngle()}deg) translateY(-50%)`
            }}
          />
          
          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 w-3 h-3 -mt-1.5 -ml-1.5 rounded-full bg-purple-500" />
          
          {/* Hour/Minute numbers */}
          {items.map((item, index) => {
            const angle = (index / 12) * 360 - 90;
            const radius = 85;
            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;
            
            const isSelected = selectingMinutes 
              ? time.minute === item 
              : time.hour === item;
            
            return (
              <button
                key={item}
                onClick={() => handleClockClick(item)}
                className={`absolute w-8 h-8 flex items-center justify-center text-sm font-medium rounded-full transition-colors
                  ${isSelected 
                    ? 'bg-purple-500 text-white' 
                    : 'text-gray-700 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900'
                  }`}
                style={{
                  left: `calc(50% + ${x}px - 16px)`,
                  top: `calc(50% + ${y}px - 16px)`
                }}
              >
                {selectingMinutes ? String(item).padStart(2, '0') : item}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Time display button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-left hover:border-purple-400 transition-colors"
      >
        <Clock className="w-4 h-4 text-gray-400" />
        <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
          {displayTime}
        </span>
      </button>

      {/* Time picker modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-xs p-0 overflow-hidden">
          <div className="bg-gray-100 dark:bg-gray-800 p-4">
            {/* Header with time display */}
            <div className="flex items-center justify-center gap-1 mb-4">
              <button
                onClick={() => setSelectingMinutes(false)}
                className={`text-5xl font-light px-3 py-2 rounded-lg transition-colors
                  ${!selectingMinutes 
                    ? 'bg-purple-200 dark:bg-purple-900 text-purple-800 dark:text-purple-200' 
                    : 'text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                {String(time.hour).padStart(2, '0')}
              </button>
              <span className="text-5xl font-light text-gray-800 dark:text-gray-200">:</span>
              <button
                onClick={() => setSelectingMinutes(true)}
                className={`text-5xl font-light px-3 py-2 rounded-lg transition-colors
                  ${selectingMinutes 
                    ? 'bg-purple-200 dark:bg-purple-900 text-purple-800 dark:text-purple-200' 
                    : 'text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                {String(time.minute).padStart(2, '0')}
              </button>
              
              {/* AM/PM toggle */}
              <div className="flex flex-col ml-2 text-sm">
                <button
                  onClick={() => togglePeriod('AM')}
                  className={`px-3 py-1 rounded-t-md font-medium transition-colors
                    ${time.period === 'AM' 
                      ? 'bg-pink-400 text-white' 
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                >
                  AM
                </button>
                <button
                  onClick={() => togglePeriod('PM')}
                  className={`px-3 py-1 rounded-b-md font-medium transition-colors border-t border-gray-200 dark:border-gray-600
                    ${time.period === 'PM' 
                      ? 'bg-pink-400 text-white' 
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                >
                  PM
                </button>
              </div>
            </div>

            {/* View mode toggle */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                onClick={() => setViewMode('clock')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'clock' 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                <Clock className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('input')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'input' 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                <span className="text-xs font-bold">123</span>
              </button>
            </div>

            {/* Clock dial view */}
            {viewMode === 'clock' && <ClockDial />}

            {/* Input view */}
            {viewMode === 'input' && (
              <div className="flex items-center justify-center gap-2 py-4">
                {/* Hour input */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => handleHourChange(time.hour + 1)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <input
                    type="text"
                    value={String(time.hour).padStart(2, '0')}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val >= 1 && val <= 12) handleHourChange(val);
                    }}
                    className="w-16 text-center text-3xl font-light bg-purple-200 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-lg py-2"
                  />
                  <button
                    onClick={() => handleHourChange(time.hour - 1)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <span className="text-xs text-gray-500 mt-1">Hour</span>
                </div>

                <span className="text-3xl font-light text-gray-600 dark:text-gray-300">:</span>

                {/* Minute input */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => handleMinuteChange(time.minute + 1)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <input
                    type="text"
                    value={String(time.minute).padStart(2, '0')}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val >= 0 && val <= 59) handleMinuteChange(val);
                    }}
                    className="w-16 text-center text-3xl font-light bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg py-2 border border-gray-300 dark:border-gray-600"
                  />
                  <button
                    onClick={() => handleMinuteChange(time.minute - 1)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <span className="text-xs text-gray-500 mt-1">Minute</span>
                </div>

                {/* AM/PM toggle for input view */}
                <div className="flex flex-col ml-2">
                  <button
                    onClick={() => togglePeriod('AM')}
                    className={`px-3 py-2 rounded-t-md font-medium text-sm transition-colors
                      ${time.period === 'AM' 
                        ? 'bg-pink-400 text-white' 
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                  >
                    AM
                  </button>
                  <button
                    onClick={() => togglePeriod('PM')}
                    className={`px-3 py-2 rounded-b-md font-medium text-sm transition-colors border-t border-gray-200 dark:border-gray-600
                      ${time.period === 'PM' 
                        ? 'bg-pink-400 text-white' 
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                  >
                    PM
                  </button>
                </div>
              </div>
            )}

            {/* Footer with actions */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setViewMode(viewMode === 'clock' ? 'input' : 'clock')}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
              >
                {viewMode === 'clock' ? (
                  <span className="text-xs font-bold text-gray-600 dark:text-gray-300">⌨</span>
                ) : (
                  <Clock className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                )}
              </button>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsOpen(false)}
                  className="text-purple-600 hover:text-purple-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setIsOpen(false)}
                  className="text-purple-600 hover:text-purple-700 bg-transparent hover:bg-purple-50"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimePicker;
